import { Draggable } from 'components/Draggable'
import { useKestrel } from 'features/kestrelViewer'
import { CloseButton } from 'features/kestrelViewer/ui/popupMenu/shared/CloseButton'
import React, { ReactNode } from 'react'
import styled, { keyframes } from 'styled-components'
import { KVMarkup } from 'types'

const fadeInPopup = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
  to {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
`

const PopupContainer = styled.div<{
  $width?: string
  $scrollable?: boolean
}>`
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem;
  color: white;
  line-height: 1rem;
  width: ${(p) => p.$width ?? '15rem'};
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: ${(p) => (p.$scrollable ? 'auto' : 'visible')};
  overflow-x: ${(p) => (p.$scrollable ? 'hidden' : 'visible')};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
  opacity: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${fadeInPopup} 0.3s ease-out forwards;
`

const DraggableHandle = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 60px;
  height: 4px;
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.6);
`

export const InputGroup = styled.div`
  margin-bottom: 0.5rem;
  text-align: left;
`

export const InputHeader = styled.div`
  font-size: 0.65rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`

export const SectionHeader = styled.div`
  font-size: 0.85rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
`

export const CenteredContent = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

export const Button = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #666666;
  background-color: #000000;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  flex: 1;

  &:hover {
    background-color: #444444;
    border-color: #888888;
  }
`

export const DeleteButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid rgba(220, 38, 38, 0.6);
  background-color: rgba(220, 38, 38, 0.1);
  border-radius: 4px;
  color: rgba(248, 113, 113, 0.9);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  flex: 1;

  &:hover {
    background-color: rgba(220, 38, 38, 0.2);
    border-color: rgba(220, 38, 38, 0.8);
    color: rgba(252, 165, 165, 1);
  }
`

export const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  width: 100%;
`

export const FooterSection = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 1rem;
  margin-top: 0.5rem;
`

export interface MarkupPopupContainerProps {
  popupMenuStartPosition: { x: number; y: number }
  setSelectedMarkup: React.Dispatch<React.SetStateAction<KVMarkup | undefined>>
  children: ReactNode
  width?: string
}

export const MarkupPopupContainer: React.FC<MarkupPopupContainerProps> = ({
  popupMenuStartPosition,
  setSelectedMarkup,
  children,
  width,
}) => {
  const { gatekeeper } = useKestrel()

  return (
    <Draggable
      style={{
        left: popupMenuStartPosition.x + 'px',
        top: popupMenuStartPosition.y + 'px',
        zIndex: 1000,
      }}>
      <PopupContainer
        $width={width}
        $scrollable={gatekeeper.annotationForm.annotationMenuScrollable}>
        <DraggableHandle />
        <CloseButton
          setSelectedMarkup={setSelectedMarkup}
          style={{
            top: '8px',
            right: '10px',
          }}
        />
        {children}
      </PopupContainer>
    </Draggable>
  )
}
